/**
 * Predicting plot width before the first render.
 *
 * The value axis on a horizontal bar chart is width-dependent (see
 * `VALUE_AXIS_MIN_TICK_SPACING_PX`), so tick selection needs the plot width — but the
 * plot width depends on how wide the category labels render, which depends on the
 * font. Patching the axis *after* Highcharts lays out was tried and rejected: the
 * extra redraw perturbed the rendering even when the tick values were provably
 * correct (see DEVLOG 2026-08-04).
 *
 * So we predict it instead. d3 has no layout engine to defer to and will need this
 * same calculation, which is why it lives here rather than in a renderer.
 */

/* ------------------------------------------------------------------ *
 * Text measurement
 * ------------------------------------------------------------------ */

let measureCanvas: HTMLCanvasElement | null = null;

/**
 * Resolve a CSS custom property to a concrete family list. Chart options carry
 * `var(--an26-font-mono), monospace`, which canvas cannot parse.
 */
export function resolveFontStack(fontStack: string): string {
  if (typeof document === "undefined") return fontStack;
  return fontStack.replace(/var\(\s*(--[\w-]+)\s*\)/g, (whole, name: string) => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || whole;
  });
}

/**
 * Width in px of `text` rendered in the given font. Returns 0 outside a browser so
 * server-side callers degrade rather than throw.
 */
export function measureTextWidth(
  text: string,
  opts: { fontStack: string; fontSizePx: number; fontWeight?: string | number }
): number {
  if (typeof document === "undefined") return 0;
  measureCanvas ??= document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) return 0;
  const weight = opts.fontWeight ?? "normal";
  ctx.font = `${weight} ${opts.fontSizePx}px ${resolveFontStack(opts.fontStack)}`;
  return ctx.measureText(text).width;
}

/* ------------------------------------------------------------------ *
 * Horizontal bar plot width
 * ------------------------------------------------------------------ */

/**
 * Space reserved to the right of the plot: `spacingRight` (32) plus room for the
 * final value label to overhang the axis end. Measured as a constant 45.2px across
 * every captured surface — 1232, 720, 600 and 327px wide — so it is treated as one.
 */
export const HORIZONTAL_BAR_RIGHT_GAP_PX = 45.2;

/**
 * Category labels are capped at this fraction of chart width, after which they are
 * truncated. Derived from the mobile capture: at 327px the label box is 97.7px, and
 * 0.3 × 327 = 98.1. Wider surfaces never reach the cap, which is why plotLeft is
 * identical at 1232, 720 and 600px.
 *
 * Another undocumented Highcharts behavior we now own outright, per the option-2
 * decision — declared here rather than imitated.
 */
export const CATEGORY_LABEL_MAX_WIDTH_RATIO = 0.3;

/**
 * Gap between the category label and the axis line, as used for *tick selection*.
 *
 * Deliberately 0: this predictor is verified against the baselines for choosing the
 * tick interval, and widening it changes that input and breaks the Highcharts gate.
 *
 * Note the d3 renderer needs a different number for *drawing*. The 183.7 figure the
 * unit tests pin is the tick label's bounding-box left edge, not plotLeft — the bars
 * actually start at x=187. Those are two distinct quantities and conflating them cost
 * a broken gate; the drawing offset lives in the d3 renderer instead.
 */
export const CATEGORY_LABEL_GAP_PX = 0;

/* ------------------------------------------------------------------ *
 * Line chart plot width
 * ------------------------------------------------------------------ */

/** Highcharts' default gap between a y-axis label and the plot edge (`labels.x: -8`). */
export const Y_AXIS_LABEL_GAP_PX = 8;

/** Below this width the line chart's responsive rule tightens spacing and type. */
export const LINE_CHART_COMPACT_MAX_WIDTH = 560;

export interface LineChartPlotWidthOptions {
  chartWidth: number;
  /** Rendered y-axis label texts, e.g. ["$100,000", …, "$300,000"]. */
  yAxisLabels: readonly string[];
  /** True for the embedded (tabbed pane) layout, which uses tighter spacing. */
  embedded?: boolean;
  measure?: (text: string, fontSizePx: number) => number;
}

/**
 * Predicted plot width for the line chart.
 *
 * Spacing follows the same rules the chart config uses: 20/120 standalone, 0/48
 * embedded, and 12/16 once the responsive breakpoint engages.
 *
 * Tolerance is comfortable here — desktop computes to ~3.98 months against a 4-month
 * step, so a ~25px error would not change the selection. That is a much softer target
 * than the horizontal bar, where mobile sits near an interval boundary.
 */
export function lineChartPlotWidth(opts: LineChartPlotWidthOptions): {
  plotLeft: number;
  plotWidth: number;
  compact: boolean;
} {
  const { chartWidth, yAxisLabels, embedded = false, measure } = opts;
  const compact = chartWidth <= LINE_CHART_COMPACT_MAX_WIDTH;
  const fontSizePx = compact ? 10 : 11;

  const spacingLeft = compact ? 12 : embedded ? 0 : 20;
  const spacingRight = compact ? 16 : embedded ? 48 : 120;

  const measureLabel =
    measure ??
    ((text: string, size: number) =>
      measureTextWidth(text, {
        fontStack: "var(--an26-font-mono), monospace",
        fontSizePx: size,
        fontWeight: "300",
      }));

  const widest = yAxisLabels.reduce((max, l) => Math.max(max, measureLabel(l, fontSizePx)), 0);
  const plotLeft = spacingLeft + widest + Y_AXIS_LABEL_GAP_PX;
  const plotWidth = Math.max(0, chartWidth - plotLeft - spacingRight);
  return { plotLeft, plotWidth, compact };
}

export interface HorizontalBarPlotWidthOptions {
  /** Rendered width of the chart container in px. */
  chartWidth: number;
  /** Category names shown along the axis. */
  categories: readonly string[];
  /** `chart.spacingLeft` for this chart (20 today). */
  spacingLeft: number;
  /** Injectable so tests can run without a browser. */
  measure?: (text: string) => number;
}

/**
 * Predicted `{ plotLeft, plotWidth }` for the inverted bar chart.
 *
 * Verified against the frozen baselines at four surfaces:
 *   1232 → plotLeft 183.7, plotWidth 1003.1
 *    720 → plotLeft 183.7, plotWidth  491.1
 *    600 → plotLeft 183.7, plotWidth  371.1   (split layout, tabbed shell)
 *    327 → plotLeft 117.7, plotWidth  164.1   (label cap engaged)
 */
export function horizontalBarPlotWidth(opts: HorizontalBarPlotWidthOptions): {
  plotLeft: number;
  plotWidth: number;
} {
  const { chartWidth, categories, spacingLeft, measure } = opts;

  const measureLabel =
    measure ??
    ((text: string) =>
      measureTextWidth(text, {
        fontStack: "var(--an26-font-mono), monospace",
        fontSizePx: 11,
        fontWeight: "300",
      }));

  const widest = categories.reduce((max, c) => Math.max(max, measureLabel(c)), 0);
  const labelBox = Math.min(
    widest + CATEGORY_LABEL_GAP_PX,
    chartWidth * CATEGORY_LABEL_MAX_WIDTH_RATIO
  );

  const plotLeft = spacingLeft + labelBox;
  const plotWidth = Math.max(0, chartWidth - plotLeft - HORIZONTAL_BAR_RIGHT_GAP_PX);
  return { plotLeft, plotWidth };
}
